// Core
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {NavLink, useLocation} from 'react-router-dom';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import {
  AbsoluteCenter,
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Divider,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Image,
  IconButton,
  Link,
  Text,
  Select,
  Checkbox, 
  CheckboxGroup,
  Stack,
  useDisclosure,
  useColorModeValue,
  useColorMode
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import { validateEmail, isValidPassword } from 'services/Utils';
import logo from 'assets/img/logo.png';

// Components
import { StarsIcon, EmailIcon, PhoneIcon } from 'components/Icons/Icons';
import EditField from 'components/Form/EditField';
import EditSelect from 'components/Form/EditSelect';
import EditPhone from 'components/Form/EditPhone';
import ErrorCard from 'components/Form/Errors';
import CodeModal from 'components/Modals/CodeModal';

function SignUp({setStep, newObject, setNewObject, onOpenFinish, topLine}) {
  const textColor = useColorModeValue('#0E1633', 'white');
  const textSigninColor = useColorModeValue('#0E1633', '#9FABCB');
  const borderColor = useColorModeValue('gray.200');
  const errorLine = 'linear-gradient(90deg, rgba(253, 43, 42, 0.00) 9.63%, #FD2B2A 37.41%, #F88 50%, #FD2B2A 60.02%, rgba(253, 43, 42, 0.00) 91.14%)';
  
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const [showPass, setShowPass] = React.useState(false);
  const handlePassClick = () => setShowPass(!showPass);
  const [showConfPass, setShowConfPass] = React.useState(false);
  const handleConfPassClick = () => setShowConfPass(!showConfPass);

  const [newObjectErrors, setNewObjectErrors] = useState([]);
  const [errorCard, setErrorCard] = useState();
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [buttonContinueDisable, setButtonContinueDisable] = useState(true);
  const [agreeTerms, setAgreeTerms] = useState(true);

  useMemo(() => {
    setErrorCard();
  }, [newObject]);

  const isValidInput = useMemo(() => {
    const fields = [
      'hash',
      'firstName',
      'lastName',
      'phone',
      'email',
      //'birthDate',
      //'gender',
      'password',
      'confirmPassword',
      'username',
    ];

    return fields.reduce((acc, field) => {
      acc[field] = !newObjectErrors.includes(field);
      return acc;
    }, {});
  }, [newObjectErrors]);

  const setData = useCallback(
    (maxValue, target, key) => {
      if (target.length < maxValue) {
        setNewObjectErrors([...newObjectErrors, key]);
      } else {
        setNewObjectErrors(newObjectErrors.filter((error) => error !== key));
      }
      setNewObject({ ...newObject, [key]: target });
    },
    [newObject, newObjectErrors, setNewObject, setNewObjectErrors],
  );

  const handleField = useMemo(() => {
    const fieldConfigs = [
      { field: 'hash', maxValue: 21 },
      { field: 'firstName', maxValue: 1 },
      { field: 'lastName', maxValue: 1 },
      { field: 'phone', maxValue: 8 },
      { field: 'email', maxValue: 1 },
      //{ field: 'birthDate', maxValue: 1 },
      //{ field: 'gender', maxValue: 1 },
      { field: 'password', maxValue: 1 },
      { field: 'confirmPassword', maxValue: 1 },
      { field: 'username', maxValue: 1 },
    ];

    return fieldConfigs.reduce((acc, { field, maxValue }) => {
      acc[field] = (e) => setData(maxValue, e.target.value, field);
      return acc;
    }, {});
  }, [setData]);

  useEffect(() => {
    console.log(newObject);
    if(Object.keys(newObject).length === 8){
      setButtonContinueDisable(false);
    } else {
      setButtonContinueDisable(true);
    }
  }, [newObject, handleField]);

  const history = useHistory();
  const handleCreateObject = useCallback(async () => {
    setIsLoadingButton(true);

    console.log(newObject);
    
    const errors = [];

    if (!newObject.email || !validateEmail(newObject.email)) {
      errors.push('email');
    }

    if (!newObject.password || newObject.password.length < 8 || !isValidPassword(newObject.password)) {
      errors.push('password');
    }

    if (newObject.password !== newObject.confirmPassword) {
      errors.push('confirmPassword');
    }

    if (!newObject.firstName || newObject.firstName.length < 2) {
      errors.push('firstName');
    }

    if (!newObject.lastName || newObject.lastName.length < 2) {
      errors.push('lastName');
    }

    if (!newObject.username || newObject.username.length < 2) {
      errors.push('username');
    }

    setNewObjectErrors(errors);

    if (errors.length > 0) {
      setButtonContinueDisable(false);
      setIsLoadingButton(false);
      return;
    }

    try {
      const response = await apiService.registration({
        ...newObject,
        gender: 'Unknown'
      });
      if (response.data === false || response.statusCode !== 200) {
        setErrorCard(response.message);
        setButtonContinueDisable(false);
        setIsLoadingButton(false);
        return;
      }
    } catch (e) {
      setButtonContinueDisable(false);
      setIsLoadingButton(false);
      return;
    }

    setButtonContinueDisable(false);
    setIsLoadingButton(false);
    console.log(newObject);
    setStep(3);
    onOpenFinish();
    return;
  }, [history, newObject, newObjectErrors]);

  // Popup Phone and preregistration
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [seconds, setSeconds] = useState(59);
  const [timerActive, setTimerActive] = useState(false);
  const [inputPhoneDisable, setInputPhoneDisable] = useState(false);
  useEffect(() => {
      let interval = null;
      
      if (timerActive && seconds > 0) {
        interval = setInterval(() => {
          setSeconds(seconds => seconds - 1);
        }, 1000);
      } else if (!timerActive && seconds !== 0) {
        clearInterval(interval);
      } else if (seconds === 0) {
        setTimerActive(false);
      }

      return () => clearInterval(interval);
  }, [timerActive, seconds]);
  const [isValidCode, setIsValidCode] = useState(true);
  const startTimer = () => {
      setTimerActive(true);
      setSeconds(59);  
  };
  const handlePhone = async (code) => {
    setErrorCard(false);

    if (newObjectErrors.length > 0) {
      setErrorCard(true);
      return;
    }

    let data = newObject;
    if(code){
      data = { ...data, code};
    }
    if(newObject.hash){
      data = { ...data, hash: newObject.hash};
    }

    try {
      const response = await apiService.registrationValidatePhone(data);
      if(response.statusCode === 403){
        setErrorCard(response?.message);
        setTimeout(function() { onClose(); }, 1000);
        return;
      } else if(response.data === false || 
        response.data.status === false || 
        response.statusCode !== 200 || 
        response.statusCode === 404){
        setErrorCard(response.data.errorMessage);
        setIsValidCode(false);
        return;
      } else if(response.statusCode === 200 && response.data.status === true){
        startTimer(); // stop
        startTimer(); // start
        if(response.data.hash){
          setNewObject({...newObject, hash: response.data.hash });
        }
        setInputPhoneDisable(true);
      }
    } catch(e){
      setErrorCard('This phone already exists or an unknown error occurred, try changing the fields or refreshing the page');
      setTimeout(function() { onClose(); }, 1000);
      return;
    }

    if(code){
      onClose();    
    }
  };

  return (
    <Box 
      w='100%' 
      textAlign="center" 
      overflow="scroll" 
      sx={{
        scrollbarWidth: 'none', 
        msOverflowStyle: 'none', 
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
      h="100%" 
      py="80px">
      <Box mb={{base: '50px', sm: '60px', md: '79px'}} display="flex" alignItems="center" justifyContent="center">
        <NavLink to="/"><Image src={logo} alt='lmctplus image' maxWidth="184px"/></NavLink>
        { isDark && <Box bg="rgba(64, 155, 255, 0.82)" zIndex="-1" position="absolute" opacity="0.6" filter="blur(70px)" h="300px" w="200px" mt="10%" borderRadius="full" /> }
      </Box>

      <Box position="relative">
        <Text as="h1" variant="header-gradient" 
          fontSize={{base: '32px', sm: '40px', md: '48px'}}
          >
          Create a Free</Text>
        <Box bg={topLine} left="50%" transform="translateX(-50%) rotate(180deg)" bottom="-2px" h="1px" position="absolute" w="50%" />
      </Box>

      <Flex 
          display="inline-flex"
          gap="8px"
          mt="8px"
          alignItems="center">
          <StarsIcon fontSize="24px" />
          <Box borderRadius={{base: '8px', md: '16px'}} p="4px 14px 4px 16px" background="white">
            <Text as="h2" variant="header-gradient" fontSize={{base: '26px', sm: '32px', md: '40px'}} pr="3px">Play Account</Text>
          </Box>
          <StarsIcon fontSize="24px" />
      </Flex>
      <Text fontSize={{base: '16px', md: '20px'}} color={textColor} mt="14px">Play games and explore the app for free. <br />Upgrade anytime for full access.</Text>

      <Box maxW={{sm: '100%', md: '81%'}} m="0 auto" pt={{base: '24px', md: '48px'}}>
        <FormControl id="firstName">
          <Input
            borderColor={borderColor}
            type='text'
            placeholder='First Name'
            autoComplete="off"
            value={newObject?.firstName}
            isValid={isValidInput.firstName}
            onChange={handleField.firstName}
          />
          <Box w="100%" zIndex="1" transition="0.2s all" position="absolute" bottom="0" h="2px" bg={errorLine} 
            opacity={ !isValidInput.firstName ? '1' : '0'} />
        </FormControl>

        <FormControl id="lastName" mt="16px">
          <Input
            borderColor={borderColor}
            type='text'
            placeholder='Last Name'
            autoComplete="off"
            value={newObject?.lastName}
            isValid={isValidInput.lastName}
            onChange={handleField.lastName}
          />
          <Box w="100%" zIndex="1" transition="0.2s all" position="absolute" bottom="0" h="2px" bg={errorLine} 
            opacity={ !isValidInput.lastName ? '1' : '0'} />
        </FormControl>

        <FormControl id="username" mt="16px">
          <Input
            borderColor={borderColor}
            type='text'
            placeholder='Username'
            autoComplete="off"
            value={newObject?.username}
            isValid={isValidInput.username}
            onChange={handleField.username}
          />
          <Box w="100%" zIndex="1" transition="0.2s all" position="absolute" bottom="0" h="2px" bg={errorLine} 
            opacity={ !isValidInput.username ? '1' : '0'} />
        </FormControl>

        <FormControl mt="16px" id="email">
          <Input
            borderColor={borderColor}
            type="email"
            placeholder="Email"
            autoComplete="off"
            value={newObject?.email}
            onChange={handleField.email}
          />
          <Box w="100%" zIndex="1" transition="0.2s all" position="absolute" bottom="0" h="2px" bg={errorLine} 
            opacity={ !isValidInput.email ? '1' : '0'} />
        </FormControl>

        <Box 
          mt="16px" 
          zIndex="2" 
          position="relative" 
          w="100%">
          <EditPhone editMode={true} id="phone" label="Phone Number" type="text" 
            value={newObject.phone} isValid={true} 
            disabled={inputPhoneDisable}
            onChange={(value) => handleField.phone({ target: { value: value || '' } })}
            isValid={true} />

          <Button variant="button-sm" w="80px"
            onClick={() => {handlePhone(); onOpen(); startTimer();}} 
            position="absolute" top="10px" right="10px"
            disabled={inputPhoneDisable}
            >Verify</Button>
        </Box>

        {/*<Flex gap="16px" mt="-8px">
          <FormControl variant="floating">
            <Select 
              borderColor={borderColor}
              border="unset !important"
              _focus={{
                border: 'unset !important'
              }}
              _active={{
                border: 'unset !important'
              }}
              h="56px !important" 
              pointerEvents="unset"
              value={newObject?.gender}
              isValid={isValidInput.gender}
              onChange={handleField.gender}
              placeholder="Gender">
                {[
                  { value: 'Male', title: 'Male' },
                  { value: 'Female', title: 'Female' },
                  { value: 'Transgender', title: 'Transgender' },
                  { value: 'Unknown', title: 'Unknown' },
                ].map((option, index) => (
                      <option key={index} value={option.value}>{option.title}</option>
                ))}
            </Select>
          </FormControl>

          <FormControl id="birthDate">
            <Input
              borderColor={borderColor}
              type="text"
              placeholder="Birthday"
              autoComplete="off"
              onFocus={(e) => (e.target.type = 'date')}
              onBlur={(e) => {
                if (!e.target.value) {e.target.type = 'text';}
              }}
            />
          </FormControl>
        </Flex>*/}

        <FormControl id="password" mt="-8px">
          <InputGroup>
            <Input
              borderColor={borderColor}
              placeholder='Password'
              autoComplete="off"
              value={newObject?.password}
              isValid={isValidInput.password}
              onChange={handleField.password}
              type={showPass ? 'text' : 'password'}
            />
            <InputRightElement>
              <IconButton
                aria-label={showPass ? 'Hide password' : 'Show password'}
                icon={showPass ?  <AiOutlineEye /> : <AiOutlineEyeInvisible />}
                onClick={handlePassClick}
                mt='14px'
                mr='14px'
                color='gray.200'
                fontSize='24px'
                variant='ghost-md'
              />
            </InputRightElement>
          </InputGroup>
          <Box w="100%" zIndex="1" transition="0.2s all" position="absolute" bottom="0" h="2px" bg={errorLine} 
            opacity={ !isValidInput.password || !isValidInput.password ? '1' : '0'} />
        </FormControl>

        <FormControl id="password" mt="16px">
          <InputGroup>
            <Input
              borderColor={borderColor}
              placeholder='Confirm Password'
              autoComplete="off"
              value={newObject?.confirmPassword}
              isValid={isValidInput.confirmPassword}
              onChange={handleField.confirmPassword}
              type={showConfPass ? 'text' : 'password'}
            />
            <InputRightElement>
              <IconButton
                aria-label={showConfPass ? 'Hide password' : 'Show password'}
                icon={showConfPass ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
                onClick={handleConfPassClick}
                mt='14px'
                mr='14px'
                color='gray.200'
                fontSize='24px'
                variant='ghost-md'
              />
            </InputRightElement>
          </InputGroup>
          <Box w="100%" zIndex="1" transition="0.2s all" position="absolute" bottom="0" h="2px" bg={errorLine} 
            opacity={ !isValidInput.confirmPassword || !isValidInput.confirmPassword ? '1' : '0'} />
        </FormControl>

        <Text variant="text-sm" color={textSigninColor} p={{base: '8px 0px 0 0px' ,lg: '8px 20px 0 20px'}}>
          Your password must be at least 8 characters long, contain 1 letter, 1 uppercase letter and 1 numder
        </Text>

        <Flex justifyContent="start" w="100%" gap="12px" mt="22px" mb="70px">
          <Stack spacing={5} direction='row' alignItems="center">
              <Checkbox onChange={() => setAgreeTerms(!agreeTerms)}></Checkbox>
          </Stack>
          <Flex justifyContent="start" w="100%" gap="4px">
            <Text variant="text-md" color={textSigninColor}>I agree to the</Text>
            <Link href="/terms-and-conditions"><Text color="#099BFF">Terms and Conditions</Text></Link>
          </Flex>
        </Flex>

        <Button
          variant="button-md"
          loadingText="Log In"
          isLoading={isLoadingButton}
          disabled={isLoadingButton || (agreeTerms || buttonContinueDisable)}
          onClick={handleCreateObject}
          maxW={{base: '100%', sm: '168px'}}
          m="16px auto">Continue</Button>

        { errorCard &&
          <ErrorCard error={errorCard} maxW="100%" mb="16px"/>
        }

        <Flex justifyContent="center" w="100%" gap="4px" mb="16px">
          <Text variant="text-md" color={textSigninColor}>You already have an account?</Text>
          <Link href="/auth/login"><Text color="#099BFF">Sign in</Text></Link>
        </Flex>

        <Grid templateColumns={{ sm: '1fr', lg: '1fr 1fr ' }} gap="52px" templateRows='1fr' textAlign="left" mt="60px">
          <Box>
            <Text variant="title-md" fontWeight="700" pb="14px">Contact Us</Text>
            <Flex gap="6px" pb="10px">
              <EmailIcon fontSize="16px" />
              <Text variant="text-sm"><Link href="mailto:support@lmctplus.com" style={{color: '#099BFF', fontSize: '14px'}}>support@lmctplus.com</Link></Text>
            </Flex>
            <Flex gap="6px">
              <PhoneIcon fontSize="16px" color={isDark ? 'white' : '#0E1633'} />
              <Text variant="text-sm"><Link href="tel:+61 1300 683 688" style={{fontSize: '14px'}}>+61 1300 683 688</Link></Text>
            </Flex>
          </Box>

          <Box>
            <Text variant="text-md" pb="11px"><Link href="/terms-and-conditions">Terms & Conditions</Link></Text>
            <Text variant="text-md" pb="11px"><Link href="/privacy-policy">Privacy Policy</Link></Text>
            <Text color={isDark ? '#9FABCB' : '#262D47'}>© {new Date().getFullYear()} LMCT+</Text>
          </Box>
        </Grid>
      </Box>

      <CodeModal
        handle={handlePhone}
        seconds={seconds} 
        startTimer={startTimer} 
        timerActive={timerActive} 
        isValidCode={isValidCode} 
        setIsValidCode={setIsValidCode}
        isOpen={isOpen}
        onClose={onClose}
        errorCard={errorCard}
        type={'signup'}
        title="Set phone"
        description="A verification code has been sent to the phone you provided. Please enter it below." 
            />

    </Box>
  );
}

export default SignUp;